import { Contract } from "web3-eth-contract";
import { AbiItem } from "web3-utils";
import { services } from "../services";
import contractAddresses from "../contractData/contractAddress/addresses";
import propertyFactoryABI from "../contractData/abis/propertyFactory.json";
import kycVerificationABI from "../contractData/abis/identityVerification.json";
import complianceRegistryABI from "../contractData/abis/complianceRegistry.json";
import rentalPoolABI from "../contractData/abis/rentalAsset.json";
import resellPoolABI from "../contractData/abis/resellpool.json";
import assetImplimentationABI from "../contractData/abis/propertyAssetImplimentation.json";
import tokenABI from "../contractData/abis/token.json";

type ContractInstance<T extends AbiItem[]> = Contract<T>;

export const web3Actions = {
  getPropertyFactoryContract: async (): Promise<
    ContractInstance<typeof propertyFactoryABI>
  > => {
    try {
      const { propertyFactory } = contractAddresses();
      return await services.getContractInstance(
        propertyFactoryABI,
        propertyFactory,
      );
    } catch (error) {
      throw error;
    }
  },

  getKycVerificationContract: async (): Promise<
    ContractInstance<typeof kycVerificationABI>
  > => {
    try {
      const { identityVerification } = contractAddresses();
      return await services.getContractInstance(
        kycVerificationABI,
        identityVerification,
      );
    } catch (error) {
      throw error;
    }
  },

  getComplianceRegistryContract: async (): Promise<
    ContractInstance<typeof complianceRegistryABI>
  > => {
    try {
      const { complianceRegistry } = contractAddresses();
      return await services.getContractInstance(
        complianceRegistryABI,
        complianceRegistry,
      );
    } catch (error) {
      throw error;
    }
  },

  getRentalPoolContract: async (): Promise<
    ContractInstance<typeof rentalPoolABI>
  > => {
    try {
      const { rentalPool } = contractAddresses();
      return await services.getContractInstance(rentalPoolABI, rentalPool);
    } catch (error) {
      throw error;
    }
  },

  getResellPoolContract: async (): Promise<
    ContractInstance<typeof resellPoolABI>
  > => {
    try {
      const { resellPool } = contractAddresses();
      return await services.getContractInstance(resellPoolABI, resellPool);
    } catch (error) {
      throw error;
    }
  },

  getPropertyFactoryContractForView: async (): Promise<
    ContractInstance<typeof propertyFactoryABI> | undefined
  > => {
    const { propertyFactory } = contractAddresses();
    const response = services.getContractInstance(
      propertyFactoryABI,
      propertyFactory,
    );
    return response.then((contract) => {
      if (contract) {
        return contract;
      }
      throw new Error("Failed to get property factory contract for view");
    });
  },

  getPropertyAssetContract: async (
    contractAddress: string,
  ): Promise<ContractInstance<typeof assetImplimentationABI> | undefined> => {
    const response = services.getContractInstance(
      assetImplimentationABI,
      contractAddress,
    );
    return response.then((contract) => {
      if (contract) {
        return contract;
      }
      throw new Error("Failed to get property asset contract");
    });
  },

  getTokenContract: async (): Promise<
    ContractInstance<typeof assetImplimentationABI>
  > => {
    try {
      const { token } = contractAddresses(); // Add this line to print the contract address to the console for debugging purposes
      return await services.getContractInstance(tokenABI, token);
    } catch (error) {
      throw error;
    }
  },
};
